import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { changeState } from '@teammo/teammo-general-utils';
import history from 'app/routes/history';
import dollarsToCents from 'dollars-to-cents';
import _ from 'lodash';
import { Link } from 'react-router-dom';

import Button from 'app/components/general/Button';
import InputGroupBlock from 'app/components/general/InputGroupBlock';
import SectionGroup from 'app/components/general/SectionGroup';
import Alert from 'app/components/general/Alert';
import TeamsTableSelectable from 'app/components/teams/TeamsTableSelectable';

import * as paymentsActions from 'app/ducks/payments/actions';
import * as paymentCollectionsActions from 'app/ducks/paymentCollections/actions';
import * as teamsActions from 'app/ducks/teams/actions';
import * as clubsActions from 'app/ducks/clubs/actions';
import * as teamMembersActions from 'app/ducks/teamMembers/actions';

import * as usersSelectors from 'app/ducks/users/selectors';
import * as clubsSelectors from 'app/ducks/clubs/selectors';
import * as paymentsSelectors from 'app/ducks/payments/selectors';
import * as paymentCollectionsSelectors from 'app/ducks/paymentCollections/selectors';
import * as teamsSelectors from 'app/ducks/teams/selectors';
import * as teamMembersSelectors from 'app/ducks/teamMembers/selectors';
import collectCategories from 'app/utils/static/collectCategories';
import validateInput from './CollectPaymentValidator';

function selector(state): any {
  return {
    payments: paymentsSelectors.getPayments(state),
    paymentCollections: paymentCollectionsSelectors.getPaymentCollections(
      state,
    ),
    teams: teamsSelectors.getTeams(state),
    users: usersSelectors.getUsers(state),
    clubs: clubsSelectors.getClubs(state),
    teamMembers: teamMembersSelectors.getTeamMembers(state),
    isLoading: state.payments.isLoading,
    currentCollectPaymentClub: paymentsSelectors.getCurrentFormPayment(state),
    errors: { ...state.payments.backendErrors },
  };
}

const actionCreators = _.merge(
  paymentsActions,
  paymentCollectionsActions,
  teamsActions,
  clubsActions,
  teamMembersActions,
);

interface ICollectPaymentClubProps {
  actions: any;
  payments: any;
  teamMembers: any;
  users: any;
  clubs: any;
  isLoading: boolean;
  teams: any;
  errors: any;
}

interface ICollectPaymentClubState {
  collectCategory: string;
  description: string;
  amount: string;
  floatAmount: string;
  dueAt: string;
  status: string;
  changed: boolean;
  selectedTeamIds: string[];
  errors: any;
}

@(connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
})) as any)
export default class CollectPaymentClub extends React.Component<
ICollectPaymentClubProps,
ICollectPaymentClubState
> {
  static defaultProps: ICollectPaymentClubProps = {
    actions: undefined,
    payments: undefined,
    users: undefined,
    clubs: undefined,
    teamMembers: undefined,
    isLoading: false,
    teams: undefined,
    errors: {},
  };

  state: ICollectPaymentClubState = {
    collectCategory: undefined,
    description: '',
    amount: '',
    floatAmount: '',
    dueAt: '',
    status: '',
    selectedTeamIds: this.props.teams.teamIds || [],
    changed: false,
    errors: {},
  };

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed) {
      return {
        ...prevState,
        description: nextProps.currentCollectPaymentClub.description || '',
        errors: nextProps.errors,
      };
    }
    return null;
  }

  componentDidMount(): void {
    this.props.actions.getAllTeams(
      {
        filter: { 'club-id': this.props.clubs.currentClubId },
        page: {
          size: 200,
        },
      },
      false,
    );

    this.props.actions.getAllTeamMembers(
      {
        filter: { 'club-id': this.props.clubs.currentClubId },
        page: {
          size: 200,
        },
      },
      false,
    );
  }

  handleCancel = (e): void => {
    e.preventDefault();
    history.push('/dashboard');
  };

  handleAmountChange = (values): void => {
    const { formattedValue, value } = values;
    this.setState({
      amount: formattedValue,
      floatAmount: value,
      changed: true,
      errors: {},
    });
  };

  handleChange = (e): void => {
    e.persist();
    this.setState((previousState) => ({
      ...changeState(previousState, e.target),
      changed: true,
      errors: {},
    }));
  };

  handleSelectCollectCategory = (e): void => {
    this.setState({ collectCategory: e });
  };

  updateSelectTeams = (selectedTeams): any => {
    this.setState({ selectedTeamIds: selectedTeams });
  };

  handleSubmit = (e): void => {
    e.preventDefault();
    if (this.isValid() && !this.props.payments.isLoading) {
      this.setState({ changed: false });
      const teamIds = _.map(this.state.selectedTeamIds, (o) => ({ id: o }));
      const teamMembers = _.flatMap(
        this.state.selectedTeamIds,
        (o) => this.props.teams.teamsById[o].teamMembers,
      );

      const consumerIds = _.map(teamMembers, (o) => ({
        id: this.props.teamMembers.teamMembersById[o].consumer.id,
      }));
      const teamMemberIds = _.map(teamMembers, (o) => ({ id: o }));

      this.props.actions.createPayment({
        description: this.state.description,
        amount: dollarsToCents(this.state.floatAmount),
        paymentType: 'club',
        paymentCategory: 'club-collection',
        teams: teamIds,
        teamMembers: teamMemberIds,
        consumers: consumerIds,
        club: { id: this.props.clubs.currentClubId },
      });
    }
  };

  listTeams = (): any => {
    if (this.props.teams.teamIds !== undefined) {
      return this.props.teams.teamIds.map((id) => ({
        value: id,
        label: this.props.teams.teamsById[id].name,
      }));
    }
    return [];
  };

  isValid(): boolean {
    const { errors, isValid } = validateInput(this.state);

    if (!isValid) {
      this.setState({ errors });
    }

    return isValid;
  }

  render(): React.ReactNode {
    const hasTeamSelected = this.state.selectedTeamIds.length > 0;
    const { teamsById, teamIds } = teamsSelectors.getTeamsFilteredByClubById(
      this.props.teams.teamsById,
      this.props.clubs.currentClubId,
    );

    return (
      <form>
        <div className="row">
          <div className="col">
            <div className="row">
              <div className="col">
                <div className="form-description">
                  Collect money from teams direct to your club bank account.
                  They will each receive an SMS from Teammo where they can pay
                  the requested amount via credit/debit card.
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <div className="row with-margin-bottom">
                  <div className="col">
                    {_.isEmpty(this.props.users.currentUser.bankAccount.id) && (
                      <Alert>
                        You have not added a bank account to your Teammo
                        account. You may request funds from team members, but
                        you will not receive funds until you
                        {' '}
                        <Link to="/profile/bank">add a bank account</Link>
                        .
                      </Alert>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col">
                <SectionGroup>
                  <div className="row">
                    <div className="col col-12">
                      <InputGroupBlock
                        type="select"
                        name="type"
                        labelText="Type"
                        placeholder="Type"
                        tabIndex="1"
                        errors={this.state.errors}
                        value={this.state.collectCategory}
                        onChange={this.handleSelectCollectCategory}
                        options={collectCategories}
                      />
                    </div>
                    <div className="col col-12">
                      <InputGroupBlock
                        name="description"
                        labelText="Description"
                        placeholder="Match payment"
                        tabIndex="2"
                        errors={this.state.errors}
                        value={this.state.description}
                        onChange={this.handleChange}
                      />
                    </div>
                    <div className="col col-12">
                      <InputGroupBlock
                        name="amount"
                        labelText="Amount ($ per team member)"
                        placeholder="$0.00"
                        type="currency"
                        tabIndex="3"
                        errors={this.state.errors}
                        value={this.state.amount}
                        onChange={this.handleAmountChange}
                      />
                    </div>
                    <div className="col col-12">
                      <span className="form-control-label">Select Teams</span>
                      <TeamsTableSelectable
                        {...this.props.teams}
                        teamsById={teamsById}
                        teamIds={teamIds}
                        updateSelectedRows={this.updateSelectTeams}
                        actions={this.props.actions}
                        appReady={this.props.appReady}
                        persistentFilterConfig={{
                          clubId: this.props.clubs.currentClubId,
                        }}
                        selectTeam={this.props.actions.selectTeam}
                        handleCreate={this.handleCreate}
                      />
                    </div>
                  </div>
                  <div className="row with-margin-top">
                    <div className="col">
                      <div className="form-description">
                        Please note - A 1.9% + $0.20 service fee is charged to each team
                        member, on top of the amount you request.
                      </div>
                    </div>
                  </div>
                </SectionGroup>
              </div>
            </div>
          </div>
        </div>
        <div className="row with-margin-top">
          <div className="col btn-flex-group">
            <div className="btn-flex-container">
              <Button
                className="btn-success"
                type="submit"
                isLoading={this.props.payments.isLoading}
                disabled={
                  !this.state.description
                  || !this.state.amount
                  || !hasTeamSelected
                }
                onClick={this.handleSubmit}
              >
                Request Payment
              </Button>
            </div>
          </div>
        </div>
      </form>
    );
  }
}
